import React, { useMemo, useState } from 'react';
import ReactTable from 'react-table';

import {
    TRebook,
    TRebooksProps,
    TRebooksExtendingType,
    REBOOKS_EXTENDING_TYPE,
    PAGE_SIZE_OPTIONS,
} from './types';
import { createRebooksColumns } from './columns';
import { RebooksFilters } from './RebooksFilters';
import { RebooksAvailabilityModal } from './RebooksAvailabilityModal';
import { RebooksExtendConfirmationModal } from './RebooksExtendConfirmationModal';
import { useRebooks } from './useRebooks';

const RebooksPage = ({ notification }: TRebooksProps) => {
    const [availabilityModalRebook, setAvailabilityModalRebook] =
        useState<TRebook | null>(null);
    const [extendConfirmationType, setExtendConfirmationType] =
        useState<TRebooksExtendingType>(null);

    const {
        content,
        totalCount,
        pages,
        pageSize,
        loading,
        currentPage,
        allowedSortColumns,
        currentUrlParams,
        filterOptions,
        filterOptionsLoaded,
        defaultSorted,
        onFilterChange,
        onPageChange,
        onPageSizeChange,
        onSortedChange,
        selectedCount,
        selectedBookingCount,
        pageSelectionState,
        selectableOnPageCount,
        isRebookSelected,
        isRebookSelectable,
        toggleRebook,
        togglePageSelection,
        selectAllOnPage,
        clearSelection,
        extendingType,
        isRangeExtendable,
        extendAll,
        extendSelected,
    } = useRebooks({ notification });

    const tableColumns = useMemo(
        () =>
            createRebooksColumns(allowedSortColumns, {
                isRebookSelected,
                isRebookSelectable,
                toggleRebook,
                togglePageSelection,
                pageSelectionState,
                hasSelectableOnPage: selectableOnPageCount > 0,
                isRangeExtendable,
                openAvailabilityModal: setAvailabilityModalRebook,
            }),
        [
            allowedSortColumns,
            isRebookSelected,
            isRebookSelectable,
            toggleRebook,
            togglePageSelection,
            pageSelectionState,
            selectableOnPageCount,
            isRangeExtendable,
        ],
    );

    const rangeFrom = (currentPage * pageSize) + 1;
    const rangeTo = (currentPage * pageSize) + content.length;

    const rangeText = useMemo(() => {
        if (totalCount !== null && content.length > 0) {
            return `Showing ${rangeFrom}–${rangeTo} of ${totalCount} rebooks`;
        }
        if (totalCount !== null) {
            return `Showing 0 of ${totalCount} rebooks`;
        }
        return null;
    }, [rangeFrom, rangeTo, totalCount, content.length]);

    return (
        <div className="table-container">
            <RebooksFilters
                onFilterChange={onFilterChange}
                currentUrlParams={currentUrlParams}
                filterOptions={filterOptions}
                selectedCount={selectedCount}
                pageSelectionState={pageSelectionState}
                selectableOnPageCount={selectableOnPageCount}
                extendingType={extendingType}
                isRangeExtendable={isRangeExtendable}
                onSelectAllOnPage={selectAllOnPage}
                onDeselectAll={clearSelection}
                onExtendAll={() => setExtendConfirmationType(REBOOKS_EXTENDING_TYPE.ALL)}
                onExtendSelected={() => setExtendConfirmationType(REBOOKS_EXTENDING_TYPE.SELECTED)}
            />
            {totalCount !== null && (
                <div className="table-count-bar rebooks-count-bar">
                    <span>{rangeText}</span>
                </div>
            )}
            <ReactTable
                page={currentPage}
                pageSize={pageSize}
                minRows={0}
                data={content}
                pages={pages}
                columns={tableColumns}
                className="table-content rebooks-table"
                loading={loading}
                noDataText={
                    loading || !filterOptionsLoaded ? '' : 'No rebooks found'
                }
                showPaginationBottom={true}
                showPageSizeOptions={true}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                manual={true}
                defaultSorted={defaultSorted}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                onSortedChange={onSortedChange}
            />
            <RebooksAvailabilityModal
                rebook={availabilityModalRebook}
                onClose={() => setAvailabilityModalRebook(null)}
            />
            <RebooksExtendConfirmationModal
                extendType={extendConfirmationType}
                selectedBookingCount={selectedBookingCount}
                bookingsAfter={currentUrlParams.get('after')}
                bookingsBefore={currentUrlParams.get('before')}
                isExtending={extendingType !== null}
                onCancel={() => setExtendConfirmationType(null)}
                onConfirm={() => {
                    const extendPromise =
                        extendConfirmationType === REBOOKS_EXTENDING_TYPE.SELECTED
                            ? extendSelected()
                            : extendAll();
                    extendPromise?.finally(() =>
                        setExtendConfirmationType(null),
                    );
                }}
            />
            {rangeText && (
                <div className="table-count-bar rebooks-count-bar rebooks-count-bar--bottom">
                    <span>{rangeText}</span>
                </div>
            )}
        </div>
    );
};

export default RebooksPage;
